import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { useCheckoutStoreContext } from '../../context';
import { BRANCH_DELIVERY_TYPE, DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { appStore } from 'src/mobx/appStore';
import { branchStore } from 'src/mobx/branchStore';
import { IBranch } from 'src/types/branches';
import { objectMerge } from 'src/utils/formatter';
import { getTranslate, hasBranch } from 'src/utils/helpers';
import CustomerPicker, { ICustomerPickerOption } from 'src/view/components/Controls/Pickers/CustomerPicker/CustomerPicker';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { deliveryTypeStore } from 'src/view/screens/DeliveryTypeScreen/store/deliveryTypeStore';

const CheckoutCustomerMobile: React.FC = observer((): JSX.Element => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  const changeUserPhone = useCallback((phone: string) => {
    checkoutStore.changeUserInfo('phone', phone);
  }, []);

  const selectCustomer = useCallback(
    (option?: ICustomerPickerOption) => {
      checkoutStore.selectCustomer(option?.customer);

      if (option?.customer) {
        checkoutStore.changeUserInfo('name', option.customer.name || '');
        checkoutStore.changeUserInfo('phone', option.customer.mobile || '');
        checkoutStore.changeUserInfo('email', option.customer.email || '');
      }

      const { curbside } = checkoutStore.dependencies;

      if (option?.customer.curbside) {
        if (!curbside.car_brand || !curbside.car_color || !curbside.car_number || !curbside.car_type) {
          checkoutStore.changeCurbsideInfo('car_brand', option.customer.curbside.car_brand);
          checkoutStore.changeCurbsideInfo('car_color', option.customer.curbside.car_color);
          checkoutStore.changeCurbsideInfo('car_number', option.customer.curbside.car_number);
          checkoutStore.changeCurbsideInfo('car_type', option.customer.curbside.car_type);
        }
      }

      // last order handlers
      lastCustomerOrderHandler(option);
    },
    [checkoutStore.dependencies.curbside]
  );

  const lastCustomerOrderHandler = async (option?: ICustomerPickerOption) => {
    if (!option?.customer.last_order) return;

    const lastBranch: IBranch | undefined = branchStore.branches.find((branch) => branch.id === option?.customer.last_order?.branch_id);
    if (!lastBranch) return;

    checkoutStore.selectBranch(lastBranch);
    // dine in
    if (
      option?.customer.last_order.delivery_type === DELIVERY_TYPE.DINE_IN &&
      (lastBranch.delivery_type === BRANCH_DELIVERY_TYPE.BOTH || lastBranch.delivery_type === BRANCH_DELIVERY_TYPE.PICKUP) &&
      lastBranch.is_emenu &&
      appStore.settings?.is_emenu
    ) {
      checkoutStore.changeDeliveryType(DELIVERY_TYPE.PICKUP);
      checkoutStore.toggleDineIn(1);
      checkoutStore.toggleCurbside(0);
      checkoutStore.defineDinein(checkoutStore.dependencies.branch);
    }
    // delivery
    else if (
      option?.customer.last_order.delivery_type === DELIVERY_TYPE.DELIVERY &&
      (lastBranch.delivery_type === BRANCH_DELIVERY_TYPE.BOTH || lastBranch.delivery_type === BRANCH_DELIVERY_TYPE.DELIVERY)
    ) {
      checkoutStore.changeDeliveryType(DELIVERY_TYPE.DELIVERY);
      checkoutStore.toggleDineIn(0);
      checkoutStore.toggleCurbside(0);
      //zone
      if (appStore.settings?.is_cc_zone_orders && option.customer.last_order.is_zone_address) {
        checkoutStore.selectedZone = { label: getTranslate(option.customer.last_order.zone_name), value: option.customer.last_order.area_id };
      }
      // address
      if (option.customer.last_order.latitude && option.customer.last_order.longitude && option.customer.last_order.address) {
        if (appStore.settings?.is_cc_zone_orders) {
          checkoutStore.selectedAddress = {
            label: option.customer.last_order.address,
            value: option.customer.last_order.address_id,
          };
        }
        const { branch: addressBranch, area } = await deliveryTypeStore.checkAddress(
          option.customer.last_order.latitude,
          option.customer.last_order.longitude
        );
        if (addressBranch && hasBranch(addressBranch.id)) {
          checkoutStore.selectBranch(objectMerge(lastBranch, addressBranch));
          checkoutStore.selectLocation({
            area_id: area.id,
            address_id: option.customer.last_order.address_id,
            lat: option.customer.last_order.latitude,
            lng: option.customer.last_order.longitude,
            address: option.customer.last_order.address,
          });
        }
      }
    }
    // pickup
    else if (
      option?.customer.last_order.delivery_type === DELIVERY_TYPE.PICKUP &&
      (lastBranch.delivery_type === BRANCH_DELIVERY_TYPE.BOTH || lastBranch.delivery_type === BRANCH_DELIVERY_TYPE.PICKUP)
    ) {
      checkoutStore.changeDeliveryType(DELIVERY_TYPE.PICKUP);
      checkoutStore.toggleDineIn(0);
      checkoutStore.toggleCurbside(0);
    }
  };

  return (
    <div className="d-flex align-items-center mb-4">
      <Text type={TEXT.ORDER_FIELD} text="txt_mobile" className="family-bold text-size-18 font-weight-700" required />
      {checkoutStore.isDraft ? (
        <CustomerPicker
          disabled={!checkoutStore.isDraft}
          error={checkoutStore.validatorErrors.customer_phone}
          onSelect={selectCustomer}
          onChange={changeUserPhone}
          phone={checkoutStore.dependencies.user.phone}
        />
      ) : (
        <p className="family-medium color-dark text-size-16 font-weight-500">{checkoutStore.dependencies.user.phone}</p>
      )}
    </div>
  );
});

export default CheckoutCustomerMobile;
